import React from "react";
import { Calculator, Calendar, CheckCircle, AlertTriangle, Clock } from "lucide-react";
import { calcolaRiepilogoFiscale } from "../../utils/fiscalEngine";

function formatCurrency(value) {
  const num = Number(value || 0);
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  const [intPart, decPart] = abs.toFixed(2).split(".");
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${groupedInt},${decPart} €`;
}

// Determina lo stato della scadenza
function getDeadlineStatus(deadlineDate, today) {
  const deadline = new Date(deadlineDate);
  const todayDate = new Date(today);
  const diffDays = Math.ceil((deadline - todayDate) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return "past"; // Scaduto
  if (diffDays <= 30) return "upcoming"; // In arrivo (entro 30 giorni)
  return "future"; // Futuro
}

export default function FiscalSummaryCard({
  selectedYear,
  incassiAnno,
  profitabilityCoefficient,
  contributiPrecedenti,
  aliquota,
  inpsRate,
  anniAttivita,
  fiscalData = {},
  darkMode = false,
}) {
  const today = new Date().toISOString().split("T")[0];

  // Calcoli fiscali centralizzati nel motore (fonte unica).
  const r = calcolaRiepilogoFiscale({
    incassi: incassiAnno,
    coefficiente: profitabilityCoefficient,
    aliquotaInps: inpsRate / 100,
    aliquotaImposta: aliquota / 100,
    fiscalData,
  });
  const imponibileLordo = r.imponibile;
  const imposta = r.tasseLorde;
  const contributiInps = r.inpsDovuto;
  const imponibileNetto = Math.max(0, r.imponibile - (contributiPrecedenti || 0));
  const dovutoImposta = r.tasseLorde;
  const dovutoInps = r.inpsDovuto;
  const totaleTasse = r.totaleDovuto;
  const nettoDisponibile = r.nettoDisponibile;

  // Dettaglio per-slot (acconti giugno/novembre) per le card scadenza.
  const acc = fiscalData.accontiVersati || { giugno: 0, novembre: 0 };
  const accInps = fiscalData.inpsAccontiVersati || { giugno: 0, novembre: 0 };
  const saldoV = fiscalData.saldoVersato || { imposta: 0, inps: 0 };
  // Versato/Residuo dal motore (F24 pagati se presenti, altrimenti bootstrap).
  const versatoImposta = (acc.giugno || 0) + (acc.novembre || 0) + (saldoV.imposta || 0);
  const versatoInps = (accInps.giugno || 0) + (accInps.novembre || 0) + (saldoV.inps || 0);
  const versatoTotale = r.versatoTotale;
  const residuoTotale = r.residuo;
  // Quota del versato che non è documentata da nessun F24: arriva dai valori
  // dichiarati in configurazione, quindi è correggibile dall'utente.
  const versatoDaConfigurazione = r.versatoDaConfigurazione || 0;

  // Scadenze: versato per slot (no più split ingenuo 50/50)
  const scadenzaGiugno = `${selectedYear}-06-30`;
  const scadenzaNovembre = `${selectedYear}-11-30`;
  const statusGiugno = getDeadlineStatus(scadenzaGiugno, today);
  const statusNovembre = getDeadlineStatus(scadenzaNovembre, today);
  const versatoGiugno = (acc.giugno || 0) + (accInps.giugno || 0);
  const versatoNovembre = (acc.novembre || 0) + (accInps.novembre || 0);

  const getStatusStyle = (status) => {
    if (status === "past") {
      return darkMode
        ? "border-neutral-700 bg-neutral-900/50"
        : "border-neutral-200 bg-neutral-50";
    }
    if (status === "upcoming") {
      return darkMode
        ? "border-amber-600 bg-amber-900/30"
        : "border-amber-200 bg-amber-50";
    }
    return darkMode
      ? "border-neutral-800 bg-neutral-900"
      : "border-neutral-200 bg-white";
  };

  const getStatusIcon = (status) => {
    if (status === "past") {
      return <CheckCircle className={`w-4 h-4 ${darkMode ? "text-neutral-600" : "text-neutral-400"}`} />;
    }
    if (status === "upcoming") {
      return <AlertTriangle className={`w-4 h-4 ${darkMode ? "text-amber-400" : "text-amber-600"}`} />;
    }
    return <Clock className={`w-4 h-4 ${darkMode ? "text-neutral-500" : "text-neutral-500"}`} />;
  };

  const getStatusLabel = (status) => {
    if (status === "past") return "Scaduto";
    if (status === "upcoming") return "In arrivo";
    return "";
  };

  return (
    <section className={`rounded-2xl border-2 p-6 ${darkMode ? 'border-emerald-700 bg-emerald-900/20' : 'border-emerald-200 bg-emerald-50/50'}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calculator className={`w-5 h-5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
          <h2 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-emerald-300' : 'text-emerald-900'}`}>
            Riepilogo Fiscale {selectedYear}
          </h2>
        </div>
      </div>

      <p className={`text-sm mb-4 ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>
        Calcoli basati sugli incassi — Aliquota {aliquota}% — {selectedYear}, {anniAttivita}° anno
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Colonna sinistra: Dettaglio calcoli */}
        <div className={`p-4 rounded-xl ${darkMode ? 'bg-neutral-900' : 'bg-white'} border ${darkMode ? 'border-neutral-800' : 'border-neutral-200'}`}>
          <h3 className={`text-xs font-semibold uppercase tracking-wide mb-3 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
            Dettaglio Calcoli
          </h3>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className={darkMode ? 'text-neutral-400' : 'text-neutral-600'}>Incassi {selectedYear}</span>
              <span className={`font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>{formatCurrency(incassiAnno)}</span>
            </div>
            <div className="flex justify-between">
              <span className={darkMode ? 'text-neutral-500' : 'text-neutral-500'}>× Coeff. {Math.round(profitabilityCoefficient * 100)}%</span>
              <span></span>
            </div>
            <div className={`border-t pt-2 ${darkMode ? 'border-neutral-800' : 'border-neutral-200'}`}>
              <div className="flex justify-between">
                <span className={darkMode ? 'text-neutral-400' : 'text-neutral-600'}>Imponibile lordo</span>
                <span className={`font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>{formatCurrency(imponibileLordo)}</span>
              </div>
            </div>
            {contributiPrecedenti > 0 && (
              <div className="flex justify-between">
                <span className={darkMode ? 'text-neutral-500' : 'text-neutral-500'}>− Contrib. anno prec.</span>
                <span className={darkMode ? 'text-neutral-500' : 'text-neutral-500'}>-{formatCurrency(contributiPrecedenti)}</span>
              </div>
            )}
            <div className={`border-t pt-2 ${darkMode ? 'border-neutral-800' : 'border-neutral-200'}`}>
              <div className="flex justify-between">
                <span className={darkMode ? 'text-neutral-400' : 'text-neutral-600'}>Imponibile netto</span>
                <span className={`font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>{formatCurrency(imponibileNetto)}</span>
              </div>
            </div>
            <div className={`border-t pt-2 mt-2 ${darkMode ? 'border-neutral-800' : 'border-neutral-200'}`}>
              <div className="flex justify-between">
                <span className={darkMode ? 'text-neutral-400' : 'text-neutral-600'}>Imposta {aliquota}%</span>
                <span className={`font-medium ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>{formatCurrency(imposta)}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className={darkMode ? 'text-neutral-400' : 'text-neutral-600'}>INPS {inpsRate}%</span>
                <span className={`font-medium ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>{formatCurrency(contributiInps)}</span>
              </div>
            </div>
            <div className={`border-t-2 pt-2 mt-2 ${darkMode ? 'border-emerald-700' : 'border-emerald-300'}`}>
              <div className="flex justify-between">
                <span className={`font-semibold ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>TOTALE TASSE</span>
                <span className={`font-bold text-lg ${darkMode ? 'text-red-400' : 'text-red-600'}`}>{formatCurrency(totaleTasse)}</span>
              </div>
            </div>
            <div className={`border-t pt-2 mt-2 ${darkMode ? 'border-neutral-800' : 'border-neutral-200'}`}>
              <div className="flex justify-between">
                <span className={`font-semibold ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>Netto disponibile</span>
                <span className={`font-bold text-lg ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{formatCurrency(nettoDisponibile)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Colonna destra: Scadenze pagamento */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Calendar className={`w-4 h-4 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`} />
            <h3 className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
              Scadenze Pagamento
            </h3>
          </div>

          {/* Dovuto / Versato / Residuo */}
          <div className={`p-4 rounded-xl mb-3 ${darkMode ? 'bg-neutral-900 border border-neutral-800' : 'bg-white border border-neutral-200'}`}>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Dovuto</div>
                <div className={`font-bold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>{formatCurrency(totaleTasse)}</div>
              </div>
              <div>
                <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Versato</div>
                <div className="font-bold text-emerald-600">{formatCurrency(versatoTotale)}</div>
              </div>
              <div>
                <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Residuo</div>
                <div className={`font-bold ${residuoTotale > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatCurrency(residuoTotale)}</div>
              </div>
            </div>
            <div className={`mt-3 pt-3 border-t text-xs space-y-1 ${darkMode ? 'border-neutral-800 text-neutral-400' : 'border-neutral-200 text-neutral-600'}`}>
              <div className="flex justify-between"><span>Imposta sostitutiva</span><span>{formatCurrency(versatoImposta)} / {formatCurrency(dovutoImposta)}</span></div>
              <div className="flex justify-between"><span>Contributi INPS</span><span>{formatCurrency(versatoInps)} / {formatCurrency(dovutoInps)}</span></div>
              {versatoDaConfigurazione > 0 && (
                <div className={`pt-1 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                  Di cui <strong>{formatCurrency(versatoDaConfigurazione)}</strong> dichiarati in
                  configurazione, non da un F24 registrato. Se l'importo non è corretto, modificalo
                  in <em>Contributi INPS versati</em> qui sotto.
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {/* Scadenza Giugno */}
            <div className={`p-4 rounded-xl border ${getStatusStyle(statusGiugno)}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {getStatusIcon(statusGiugno)}
                  <span className={`font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                    30 Giugno {selectedYear}
                  </span>
                </div>
                {getStatusLabel(statusGiugno) && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    statusGiugno === "past"
                      ? darkMode ? "bg-neutral-800 text-neutral-500" : "bg-neutral-200 text-neutral-500"
                      : darkMode ? "bg-amber-900/50 text-amber-300" : "bg-amber-100 text-amber-700"
                  }`}>
                    {getStatusLabel(statusGiugno)}
                  </span>
                )}
              </div>
              <div className={`text-lg font-bold mb-2 ${
                statusGiugno === "past"
                  ? darkMode ? "text-neutral-600" : "text-neutral-400"
                  : darkMode ? "text-white" : "text-neutral-900"
              }`}>
                {formatCurrency(versatoGiugno)} versati
              </div>
              <div className={`text-xs space-y-1 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                <div>• Acconto imposta versato: {formatCurrency(acc.giugno || 0)}</div>
                <div>• Acconto INPS versato: {formatCurrency(accInps.giugno || 0)}</div>
              </div>
            </div>

            {/* Scadenza Novembre */}
            <div className={`p-4 rounded-xl border ${getStatusStyle(statusNovembre)}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {getStatusIcon(statusNovembre)}
                  <span className={`font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                    30 Novembre {selectedYear}
                  </span>
                </div>
                {getStatusLabel(statusNovembre) && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    statusNovembre === "past"
                      ? darkMode ? "bg-neutral-800 text-neutral-500" : "bg-neutral-200 text-neutral-500"
                      : darkMode ? "bg-amber-900/50 text-amber-300" : "bg-amber-100 text-amber-700"
                  }`}>
                    {getStatusLabel(statusNovembre)}
                  </span>
                )}
              </div>
              <div className={`text-lg font-bold mb-2 ${
                statusNovembre === "past"
                  ? darkMode ? "text-neutral-600" : "text-neutral-400"
                  : darkMode ? "text-white" : "text-neutral-900"
              }`}>
                {formatCurrency(versatoNovembre)} versati
              </div>
              <div className={`text-xs space-y-1 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                <div>• Acconto imposta versato: {formatCurrency(acc.novembre || 0)}</div>
                <div>• Acconto INPS versato: {formatCurrency(accInps.novembre || 0)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
